import classNames from "classnames";
import { Link } from "react-router-dom";
import { useContext } from "react";
import { FormattedMessage } from "react-intl";
import { UserContext } from "providers/UserProvider";

import { NavBarButtonProps } from "./types";

import { ReactComponent as ChatIcon } from "assets/images/icons/navbar/chat.svg";
import { ReactComponent as HelpIcon } from "assets/images/icons/navbar/help.svg";
import { ReactComponent as LogoutIcon } from "assets/images/icons/navbar/logout.svg";
import { ReactComponent as HomeIcon } from "assets/images/icons/navbar/home.svg";
import { ReactComponent as ProfileIcon } from "assets/images/icons/navbar/profile.svg";
import { ReactComponent as LogoIcon } from "assets/images/icons/navbar/logo.svg";
import { ReactComponent as MembersIcon } from "assets/images/icons/navbar/members.svg";
import { messages } from "./messages";
import { LocationContext } from "providers/LocationProvider";
import { useIntercomCustom } from "hooks/utils/useIntercomCustom";

const NavBarButton: React.FC<NavBarButtonProps> = ({
  icon,
  children,
  active,
  color = "--milkylb-color-secondary-100",
  ...props
}) => {
  return (
    <button
      className={classNames(
        `nav-bar__button flex w-full items-center truncate rounded-lg px-2 font-['Source_Sans_Pro'] text-base transition-all`,
        !active && "hover:bg-[--color-navbar-hover]",
      )}
      style={{
        color:
          !icon && active
            ? "var(--milkylb-color-secondary-200)"
            : `var(${color})`,
      }}
      {...props}
    >
      {icon && (
        <span
          className={classNames(
            "mr-4 p-2",
            active && "rounded-full bg-[--color-alternative-accent]",
          )}
        >
          {icon}
        </span>
      )}
      {children}
    </button>
  );
};

export const NavBarIntroducingBroker: React.FC = () => {
  const { logout } = useContext(UserContext);
  const { show } = useIntercomCustom();
  const { location } = useContext(LocationContext);

  return (
    <nav
      className={classNames(
        "fadeIn w-[calc((100vw - 60px) / 12)] flex min-w-[237px] flex-col justify-between bg-[--milkylb-color-secondary-800] px-6 py-8",
        !location.pathname.startsWith("/logged") && "fadeOut",
      )}
    >
      <div className="flex flex-col gap-1">
        <LogoIcon className="mb-8 ml-2 flex h-8 w-8 items-center justify-center" />
        <Link to="/logged/dashboard">
          <NavBarButton
            icon={<HomeIcon className="h-5 w-5" />}
            active={location.pathname.startsWith("/logged/dashboard")}
          >
            <FormattedMessage {...messages.homeLabel} />
          </NavBarButton>
        </Link>
        <Link to="/logged/members">
          <NavBarButton
            icon={<MembersIcon className="h-5 w-5 stroke-white" />}
            active={location.pathname.startsWith("/logged/members")}
          >
            <FormattedMessage {...messages.usersLabel} />
          </NavBarButton>
        </Link>
        <Link to="/logged/profile">
          <NavBarButton
            active={location.pathname.startsWith("/logged/profile")}
            icon={<ProfileIcon className="h-5 w-5" />}
          >
            <FormattedMessage {...messages.profileLabel} />
          </NavBarButton>
        </Link>
      </div>
      <div className="flex flex-col gap-1">
        <a href="https://help.letsbit.io/" target="_blank">
          <NavBarButton
            icon={<HelpIcon className="h-5 w-5" />}
            color="--milkylb-color-secondary-200"
          >
            <FormattedMessage {...messages.helpCenterLabel} />
          </NavBarButton>
        </a>
        <NavBarButton
          icon={<ChatIcon className="h-5 w-5" />}
          onClick={() => show()}
          color="--milkylb-color-secondary-200"
        >
          <FormattedMessage {...messages.contactLabel} />
        </NavBarButton>
        <NavBarButton
          icon={<LogoutIcon className="h-5 w-5" />}
          onClick={() => logout()}
          color="--milkylb-color-red-error"
        >
          <FormattedMessage {...messages.logOutLabel} />
        </NavBarButton>
      </div>
    </nav>
  );
};
